import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { addSensor, selectSensorById, updateSensor } from "./sensorsSlice";
import { MeasurmentType } from "./types";
import { Box, Button, ButtonGroup, Checkbox, Container, FormControlLabel, FormGroup, FormLabel, Paper, Stack, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Localized, useLocalization } from "@fluent/react";
import { Link } from "react-router-dom";
import SaveIcon from '@mui/icons-material/Save';

const ALL_MEASURMENT_TYPES: MeasurmentType[] = [
    'particles-in-air',
    'air-exchange-rate',
    'pressure-difference',
    'temp-and-humidity',
    'light-intensity',
    'HEPA-integrity-and-tightness',
]

export const SensorForm = () => {
    const { id: paramId } = useParams()
    const isNew = !paramId
    const entity = useAppSelector(state => paramId ? selectSensorById(state, paramId) : undefined)

    const [name, setName] = useState(entity?.name ?? '')
    const [producer, setProducer] = useState(entity?.producer ?? '')
    const [calibrationDate, setCalibrationDate] = useState<Date | null>(
        entity?.calibrationDate ? new Date(entity.calibrationDate) : null
    )
    const [measurmentTypes, setMeasurmentTypes] = useState<MeasurmentType[]>(entity?.measurmentTypes ?? [])
    const [nameError, setNameError] = useState(false)

    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const handleTypeToggle = useCallback((type: MeasurmentType) => {
        setMeasurmentTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        )
    }, [])

    const handleSave = useCallback(() => {
        if (!name.trim()) {
            setNameError(true)
            return
        }
        setNameError(false)

        if (isNew) {
            const id = crypto.randomUUID()
            dispatch(addSensor({
                id,
                name,
                producer,
                calibrationDate: calibrationDate ? calibrationDate.toISOString().split('T')[0] : null,
                measurmentTypes,
            }))
            navigate(`/measurements/sensors/edit/${id}`, { replace: true })
        } else if (entity) {
            dispatch(updateSensor({
                id: entity.id,
                changes: {
                    name,
                    producer,
                    calibrationDate: calibrationDate ? calibrationDate.toISOString().split('T')[0] : null,
                    measurmentTypes,
                },
            }))
            navigate('/measurements/sensors')
        }
    }, [isNew, name, producer, calibrationDate, measurmentTypes, entity, dispatch, navigate])

    return (
        <Container component={Paper} maxWidth={false} sx={{ paddingTop: 2, paddingBottom: 2 }}>
            <Stack spacing={2} sx={{ maxWidth: 600 }}>
                <TextField
                    required
                    label={<Localized id="sensor-name">Nazwa</Localized>}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    error={nameError}
                    inputProps={{ maxLength: 200 }}
                />
                <TextField
                    label={<Localized id="sensor-producer">Producent</Localized>}
                    value={producer}
                    onChange={e => setProducer(e.target.value)}
                    inputProps={{ maxLength: 200 }}
                />
                <DatePicker
                    label={<Localized id="sensor-calibration-date">Data kalibracji</Localized>}
                    value={calibrationDate}
                    onChange={setCalibrationDate}
                />
                <Box>
                    <FormLabel>
                        <Localized id="sensor-measurment-types">Typy pomiarów</Localized>
                    </FormLabel>
                    <FormGroup>
                        {ALL_MEASURMENT_TYPES.map(type => (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        checked={measurmentTypes.includes(type)}
                                        onChange={() => handleTypeToggle(type)}
                                    />
                                }
                                label={l10n.getString(`measurment-type-${type}`, null, type)}
                            />
                        ))}
                    </FormGroup>
                </Box>
                <ButtonGroup color="secondary" variant="contained">
                    <Button startIcon={<SaveIcon />} onClick={handleSave}>
                        <Localized id="save">Zapisz</Localized>
                    </Button>
                    <Button component={Link} to="/measurements/sensors" color="secondary" variant="outlined">
                        <Localized id="back">Wróć</Localized>
                    </Button>
                </ButtonGroup>
            </Stack>
        </Container>
    )
}
